import React from 'react';
import { BookOpen, Shield, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold">About the Ministry</h1>
            <p className="text-xl text-gray-300">
              Teaching post-tribulational truth through plain grammatical analysis of Scripture
            </p>
          </div>
        </div>
      </section>

      {/* About Mordecai */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="aspect-square bg-gray-200 rounded-lg flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxzcGlyaXR1YWwlMjBhd2FrZW5pbmd8ZW58MHx8fHwxNzczNTM1Nzg5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Mordecai"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="space-y-6">
                <Badge className="bg-amber-600 text-white">Mordecai</Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
                  Curtis Alexander Fenison
                </h2>
                <div className="prose prose-lg text-gray-700 space-y-4">
                  <p>
                    Curtis Alexander Fenison, known by his pen name Mordecai, is the founder and primary teacher of Trump of the Lord Ministries. His teaching ministry is centered on applying plain grammatical analysis to biblical prophecy, particularly eschatology.
                  </p>
                  <p>
                    After years of teaching the pre-tribulation rapture position, Mordecai began applying strict grammatical analysis to the key prophetic passages. What he discovered changed everything - the grammar consistently pointed to a post-tribulational rapture position.
                  </p>
                  <p>
                    Rather than conforming Scripture to tradition, Mordecai chose to conform his theology to what the text actually says. This commitment to grammatical integrity led to the development of the six-protocol Grammar Integrity Test (GIT) system.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Ministry's Mission */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Our Mission
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <BookOpen className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Grammatical Truth</h3>
                <p className="text-gray-600">
                  Teaching believers to read Scripture according to its plain grammatical meaning, not traditional interpretation
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Shield className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Kingdom Preparation</h3>
                <p className="text-gray-600">
                  Equipping the body of Messiah for the tribulation period through sound doctrine and practical preparation
                </p>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Heart className="h-8 w-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Unity in Truth</h3>
                <p className="text-gray-600">
                  Building bridges between pre-trib and post-trib believers through respectful, grammatical discussion
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Grammar Integrity Test */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                The Grammar Integrity Test
              </h2>
              <p className="text-xl text-gray-600">
                A systematic six-protocol approach to biblical interpretation
              </p>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p>
                The Grammar Integrity Test (GIT) is a systematic methodology for evaluating biblical passages, theological claims, and doctrinal positions based solely on the plain grammatical structure of the text.
              </p>
              <p>
                Rather than beginning with a theological position and finding verses to support it, the GIT begins with the text itself and asks six fundamental questions:
              </p>
              <ol className="space-y-3">
                <li><strong>Subject Test:</strong> Who is the text talking about?</li>
                <li><strong>Verb Test:</strong> What does the text say actually happens?</li>
                <li><strong>Timeline Test:</strong> When does the text say this takes place?</li>
                <li><strong>Location Test:</strong> Where does the text place the action?</li>
                <li><strong>Purpose Test:</strong> Why does the text say this happens?</li>
                <li><strong>Method Test:</strong> How does the text say it occurs?</li>
              </ol>
              <p>
                By systematically applying these six protocols to any passage, the GIT eliminates speculation and tradition, allowing the grammar of Scripture to speak for itself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Books */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              The Books
            </h2>
            <div className="space-y-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="aspect-[3/4] bg-gray-200 rounded-lg">
                  <img
                    src="https://images.unsplash.com/photo-1509021436665-8f07dbf5bf1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxvcGVuJTIwYmlibGV8ZW58MHx8fHwxNzczNTM1Nzk0fDA&ixlib=rb-4.1.0&q=85"
                    alt="The King's Return"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">The King's Return: The Grammar of His Coming</h3>
                  <p className="text-gray-700">
                    The flagship work presenting the post-tribulational case through plain grammatical analysis. Available in three formats: Little Book 2 (laymen's edition), scholarly manuscript, and full curriculum with 16 lessons.
                  </p>
                  <p className="text-gray-700">
                    Includes comprehensive appendices examining the four corner pieces, border pieces, and the blueprints (feast calendar connections).
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700">View in Store</Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="aspect-[3/4] bg-gray-200 rounded-lg md:order-2">
                  <img
                    src="https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxvcGVuJTIwYmlibGV8ZW58MHx8fHwxNzczNTM1Nzk0fDA&ixlib=rb-4.1.0&q=85"
                    alt="The Voice of the Spirit"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="space-y-4 md:order-1">
                  <h3 className="text-2xl font-bold text-gray-900">The Voice of the Spirit</h3>
                  <p className="text-gray-700">
                    A companion work exploring the ministry of the Holy Spirit in these last days. Book 1 is published and available, with additional volumes in development.
                  </p>
                  <p className="text-gray-700">
                    Examines the Spirit's role in preparing believers for the tribulation period and the return of Messiah.
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700">View in Store</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Begin Your Journey Into Grammatical Truth
            </h2>
            <p className="text-xl text-amber-100">
              Start with Little Book 2 completely free as a Seeker member
            </p>
            <Button size="lg" className="bg-white text-amber-700 hover:bg-gray-100">
              Start Reading Free
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
